import React, { useState, useEffect } from 'react'
import {
  TextField,
  TextInput,
  useTranslate,
  useResourceContext,
  useResourceDefinitions,
  Pagination,
  ListContextProvider,
  ListToolbar,
  useList,
  useRedirect,
  useNotify,
  NumberField,
  minValue,
  FunctionField,
} from 'react-admin'
import { Container, Box, Typography } from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { ListViewTopToolbar } from 'src/components/uiElements/CRUD/ListViewTopToolbar'
import { setResourceProps } from 'src/helper/resourceProps'
import { CustomDatagrid } from 'src/components/uiElements/styledComponents/CustomDatagrid'
import { itemServices } from 'src/services/reservation/item/item.service'
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'
import { usePaymentContext } from 'src/components/pages/payment/payment/context/paymentContext'
import { getTotal } from 'src/components/pages/payment/payment/logic/common/getTotal'
import { paymentServices } from 'src/services/payment/Payment.service'
import { extractDetails } from 'src/components/pages/payment/payment/logic/common/extracDetails'
import { reverseExchangeRate } from 'src/components/pages/payment/payment/logic/common/calculateReverseExchangeRate'
import { useErrorHandler } from 'src/hooks/useErrorHandler'
import LoadingButton from '@mui/lab/LoadingButton'
import { HasPermission, hasPermissionFunction } from 'src/helper/HasPermission'
import { PERMISSION_RECEIPT } from 'src/helper/Permission'

export const ItemPayment = () => {
  const notify = useNotify()
  const redirect = useRedirect()
  const translate = useTranslate()
  const showError = useErrorHandler()
  const [isLoading, setIsLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [data, setData] = useState([])
  const [editedData, setEditedData] = useState({})
  const [editingRows, setEditingRows] = useState([])
  const [sort, setSort] = useState({ field: 'id', order: 'ASC' })
  const [filter, setFilter] = useState({})
  const [displayedFilters, setDisplayedFilters] = useState({})
  const nameResource = useResourceContext()
  const [idEdit, setIdEdit] = useState(0)
  const [amountPending, setAmountPending] = useState(0)
  const [amountToPay, setAmountToPay] = useState(0)
  const [restAmount, setRestAmount] = useState(0)
  const [deductedAmount, setDeductedAmount] = useState(0)
  const [isNegative, setIsNegative] = useState(false)
  const {
    reservationIds,
    paymentCurrecies,
    paymentExchanges,
    setItemsSelected,
    currency,
    paymentData,
    setPaymentData,
    setPaymentCurrecies,
    setPaymentExchanges,
  } = usePaymentContext()

  const [loadingGeneratePayment, setLoadingGeneratePayment] = useState(false)
  const [loadingGeneratePaymentWithReceipt, setLoadingGeneratePaymentWithReceipt] = useState(false)
  const [currentAmoutToPaid, setCurrentAmoutToPaid] = useState(
    getTotal(paymentData) ? parseFloat(getTotal(paymentData).toFixed(5)) : 0
  )

  setResourceProps(useResourceDefinitions(), nameResource)

  const listContext = useList({
    data: data,
    sort,
    page: 1,
    perPage: 10,
    filter,
  })

  const { selectedIds, onUnselectItems } = useList({ data })

  useEffect(() => {
    if (confirm) {
      saveAmountToPay(idEdit)
      setIdEdit(0)
      setConfirm(false)
      setOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirm])

  useEffect(() => {
    if (currentAmoutToPaid < 0) {
      setIsNegative(true)
      notify(translate('ra.message.negative_amount_error'), { type: 'error' })
    } else {
      setIsNegative(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAmoutToPaid, notify])

  useEffect(() => {
    onUnselectItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getItem(reservationIds)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationIds])

  useEffect(() => {
    if (selectedIds) {
      const items = getItemSelectedAndAddPendingAmountToAmountToPay(selectedIds)
      setItemsSelected(items)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, data])

  useEffect(() => {
    // Calcular el total de `amount_to_pay` de los ítems seleccionados actualmente
    const selectedTotal = parseFloat(
      data
        .filter((item) => selectedIds.includes(item.id))
        .reduce((total, item) => total + Number(item.amount_to_pay || 0), 0)
        .toFixed(5)
    )

    // Calcular la diferencia desde el último total seleccionado
    const difference = deductedAmount - selectedTotal
    // Actualizar el monto deducido
    setDeductedAmount(selectedTotal)

    // Ajustar el `currentAmoutToPaid` por la diferencia
    setCurrentAmoutToPaid((prevAmount) => {
      const newAmount = prevAmount + difference
      return parseFloat(newAmount.toFixed(5))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, data]) // Dependencias del efecto

  const getItemSelectedAndAddPendingAmountToAmountToPay = (selectedIds) => {
    if (selectedIds.length === 0) {
      return []
    }
    return data.filter((item) => {
      if (selectedIds.includes(item.id)) {
        if (item.amount_to_pay === 0) {
          item.amount_to_pay = item?.amount_pending_converted || 0
        }
        return true
      }
      return false
    })
  }

  const getItem = async (ids) => {
    try {
      setIsLoading(true)
      let items = await itemServices.item_to_pay(
        ids,
        currency?.hash_currency,
        reverseExchangeRate(paymentExchanges)
      )
      if (items) {
        setData(formatData(items))
        setIsLoading(false)
        return
      }
      setData([])
      setIsLoading(false)
      return
    } catch (error) {
      setIsLoading(false)
      console.log('error', error)
      notify('ra.message.reservation_item_not_found', {
        type: 'error',
      })
    }
  }

  const formatData = (data) => {
    let auxData = [...data]
    return auxData.map((item) => {
      return {
        amount_to_pay: 0,
        item_id: item.item_id,
        id: item.item_id,
        product: item.product,
        external_id: item.external_id,
        contact: item.contact,
        date_in: item.date_in,
        reservation_number: item.reservation_number,
        currency: item.currency,
        account_relation: item.account_relation,
        amount_total_converted: item.amount_total_converted,
        code: item.code,
        amount_pending_converted: item.amount_pending_converted,
        gmx_segment_id: item.gmx_segment_id,
        hash_item: item?.hash_item,
        currency_converted: item.currency_converted,
      }
    })
  }

  const onShowFilter = (filterName) => {
    setDisplayedFilters((displayedFilters) => ({
      ...displayedFilters,
      [filterName]: true,
    }))
  }

  const onHideFilter = (filterName) => {
    const displayedFiltersAux = { ...displayedFilters }
    delete displayedFiltersAux[filterName]
    setDisplayedFilters(displayedFiltersAux)
    const filterAux = { ...filter }
    delete filterAux[filterName]
    setFilter(filterAux)
  }

  const onChangeFilters = (filters, displayedFiltersAux) => {
    Object.entries(filters).forEach((elem) => {
      const [key, value] = elem
      if (!value) {
        delete filters[key]
      }
    })

    setFilter(filters)
    setDisplayedFilters(displayedFiltersAux)
  }

  const onClickEdit = (id) => {
    setEditingRows((current) => [...current, id])
  }

  const validateAmountToPay = (item, amount) => {
    if (parseFloat(amount?.amount_to_pay) > parseFloat(item.amount_pending_converted)) {
      setOpen(true)
      setIdEdit(item.id)
      setAmountPending(item.amount_pending_converted)
      setAmountToPay(amount?.amount_to_pay)
      setRestAmount(
        parseFloat(parseFloat(amount?.amount_to_pay) - parseFloat(item.amount_pending_converted)).toFixed(2)
      )
      return false
    }
    return true
  }

  const saveAmountToPay = (id) => {
    const dataAux = [...data]
    const index = dataAux.findIndex((elem) => elem.id === id)
    if (index > -1) {
      Object.entries(editedData[id]).forEach((elem) => {
        const [key, value] = elem
        dataAux[index][key] = parseFloat(value)
      })
      setData(dataAux)
    }

    const editedDataAux = { ...editedData }
    delete editedDataAux[id]
    setEditedData(editedDataAux)
  }

  const onClickSave = (id) => {
    setEditingRows((current) => current.filter((elem) => elem !== id))
    if (editedData[id]) {
      let item = data.find((elem) => elem.id === id)
      let isValid = validateAmountToPay(item, editedData[id])
      if (!isValid) return
      saveAmountToPay(id)
    }
  }

  const onClickCancel = (id) => {
    const editedDataAux = { ...editedData }
    delete editedDataAux[id]
    setEditedData(editedDataAux)
    setEditingRows((current) => current.filter((elem) => elem !== id))
  }

  const onChangeInput = (id, source, value) => {
    setEditedData((current) => {
      current[id] = {
        ...current[id],
        [source]: value,
      }
      return current
    })
  }

  const isAvailable = (selectedIds, data, currentAmoutToPaid) => {
    // Verificar si la lista de datos está vacía o nula
    if (!data || data.length === 0) return true
    // Verificar si hay al menos un ítem seleccionado y currentAmoutToPaid es 0
    return selectedIds.length > 0 && currentAmoutToPaid === 0
  }

  const paymentFilters = [<TextInput label={translate('ra.field.id')} source='id' defaultValue='' />]

  const onErrorGeneratePayment = (error) => {
    showError(error, () =>
      notify('ra.message.item_payment_generate_error', {
        type: 'error',
      })
    )
    redirect('/payment-entity')
    setPaymentData(null)
  }

  const onSuccessGeneratePayment = (data) => {
      notify('ra.message.item_payment_generate_success', {
        type: 'success',
      })
  }

  const getSelectedItems = (data, selectedIds) => {
    return data?.filter((item) => selectedIds.includes(item?.id))
  }

  const returnPayment = (paymentData) => {
    if (paymentData?.form_data?.credit_or_payment_type === 'PAYMENT_TYPE') {
      return extractDetails(paymentData?.form_data?.paymentType) || []
    }
    return []
  }

  const returnCredit = (paymentData) => {
    if (paymentData?.form_data?.credit_or_payment_type === 'CREDIT') {
      return paymentData?.form_data?.credit || []
    }
    return []
  }

  const generatePayment = async () => {
    try {
      const body = {
        paymentType: returnPayment(paymentData),
        credit: returnCredit(paymentData),
        currencies_exchange: reverseExchangeRate(paymentExchanges),
        hash_business_unit: paymentData?.hash_business_unit,
        amount: getTotal(paymentData),
        generate_receipt: false,
        item: getSelectedItems(data, selectedIds),
        currency: currency,
      }
      setLoadingGeneratePayment(true)
      const generatePayment = await paymentServices.liquidateExpenses(body)
      if (generatePayment) {
        onSuccessGeneratePayment(generatePayment)
        redirect('/payment-entity')
        setPaymentData(null)
      }
    } catch (error) {
      if(error.status_code !== 422)
        onErrorGeneratePayment(error)
    } finally {
      setLoadingGeneratePayment(false)
      setPaymentCurrecies([])
      setPaymentExchanges([])
    }
  }

  const generatePaymentWithReceipt = async () => {
    try {
      const body = {
        paymentType: returnPayment(paymentData),
        credit: returnCredit(paymentData),
        currencies_exchange: reverseExchangeRate(paymentExchanges),
        hash_business_unit: paymentData?.hash_business_unit,
        amount: getTotal(paymentData),
        generate_receipt: true,
        item: getSelectedItems(data, selectedIds),
        currency: currency,
      }
      setLoadingGeneratePaymentWithReceipt(true)
      const generatePayment = await paymentServices.liquidateExpenses(body)
      if (generatePayment) {
        onSuccessGeneratePayment(generatePayment)
        redirect('/payment-entity')
        setPaymentData(null)
      }
    } catch (error) {
      onErrorGeneratePayment(error)
    } finally {
      setLoadingGeneratePaymentWithReceipt(false)
      setPaymentCurrecies([])
      setPaymentExchanges([])
    }
  }

  return (
    <Container className='content'>
      <CardCommon title={translate('ra.title.list_item_to_pay')}>
        <Box display={'flex'}>
          <Box display={'flex'} flex={1} justifyContent={'flex-start'}>
            {paymentExchanges.length > 0 && (
              <Box
                style={{
                  backgroundColor: '#bcf3bc',
                  border: '1px solid green',
                  borderRadius: '10px',
                  padding: '5px',
                }}
              >
                {paymentExchanges
                  .filter((currency) => !currency?.is_reverse)
                  .map((currency, index) => (
                    <div key={index}>
                      <Typography component='span' fontWeight='bold'>
                        {currency?.currency_relation?.short_name}-{currency?.currency_to_relation?.short_name}
                        : {currency.value}
                      </Typography>
                    </div>
                  ))}
              </Box>
            )}
          </Box>
          <Box
            display={'flex'}
            flex={1}
            flexDirection={'column'}
            justifyContent={'center'}
            alignItems={'flex-end'}
          >
            <Typography variant='h6' component='h2'>
              {translate('ra.field.total')} {currency?.short_name} :{' '}
              {numberFormatterWithDecimalAndThousand(
                getTotal(paymentData) && Math.round(getTotal(paymentData) * 100) / 100
              )}
            </Typography>
            <Typography variant='h6' component='h2' style={{ color: isNegative ? 'red' : 'inherit' }}>
              {translate('ra.field.amount_to_apply')} {currency?.short_name} :{' '}
              {numberFormatterWithDecimalAndThousand(currentAmoutToPaid)}
            </Typography>
          </Box>
        </Box>

        <ListContextProvider
          value={{
            ...listContext,
            displayedFilters: displayedFilters,
            setFilters: onChangeFilters,
            showFilter: onShowFilter,
            hideFilter: onHideFilter,
            isLoading: isLoading,
            selectedIds: selectedIds,
          }}
        >
          {data && (
            <>
              <div>
                <ListToolbar
                  actions={<ListViewTopToolbar exporterDisable={true} />}
                  filters={paymentFilters}
                />
                <CustomDatagrid
                  setSort={setSort}
                  bulkActionButtons={<></>}
                  editedData={editedData}
                  editingRows={editingRows}
                  onClickEdit={onClickEdit}
                  onClickSave={onClickSave}
                  onClickCancel={onClickCancel}
                  onChangeInput={onChangeInput}
                >
                  <TextField label={translate('ra.field.id_reserva')} source='reservation_number' />
                  <TextField label={translate('ra.field.id_item')} source='id' />
                  <TextField label={translate('ra.field.product')} source='product' />
                  <TextField label={translate('ra.field.locator')} source='code' />
                  <TextField label={translate('ra.field.currency')} source='currency.short_name' />
                  <FunctionField
                    label={translate('ra.field.amount_in_currency_to_pay')}
                    render={(record) =>
                      `${numberFormatterWithDecimalAndThousand(record?.account_relation?.amount_total, 4)}`
                    }
                  />
                  <TextField
                    label={translate('ra.field.currency_pay')}
                    source='currency_converted.currency_to_relation.short_name'
                  />
                  {/* <DateField label={translate('ra.field.checkin')} source='date_in' /> */}
                  <FunctionField
                    label={translate('ra.field.total_amount_in_currency_to_pay')}
                    render={(record) =>
                      `${numberFormatterWithDecimalAndThousand(record?.amount_total_converted, 4)}`
                    }
                  />
                  <FunctionField
                    label={translate('ra.field.pending_amount_in_currency_to_pay')}
                    render={(record) =>
                      `${numberFormatterWithDecimalAndThousand(record?.amount_pending_converted, 4)}`
                    }
                  />

                  {/* <FunctionField
                    label={translate('ra.field.amount_pending')}
                    render={(record) =>
                      `${numberFormatterWithDecimalAndThousand(record?.account_relation?.amount_pending, 4)}`
                    }
                  /> */}

                  <NumberField
                    label={translate('ra.field.amount_to_pay')}
                    source='amount_to_pay'
                    editable='true'
                    defaultValue={0}
                    validate={[minValue(0.00001)]}
                  />
                  <div label={translate('ra.field.action')} buttons='true' />
                </CustomDatagrid>
                <Pagination />
              </div>

              {paymentCurrecies.length > 0 && (
                <Box display={'flex'} margin={'20px'}>
                  <Box flex={1} display={'flex'} justifyContent={'flex-start'}>
                    <LoadingButton
                      className='button'
                      variant='contained'
                      onClick={() => {
                        redirect('/payment-entity/payment')
                        setPaymentData({ ...paymentData, is_back: true })
                      }}
                      style={{ margin: '10px' }}
                    >
                      {translate('ra.action.back')}
                    </LoadingButton>
                  </Box>
                  <Box flex={1} display={'flex'} justifyContent={'flex-end'}>
                    {paymentData?.form_data?.credit_or_payment_type !== 'CREDIT' && (
                      <HasPermission permission={PERMISSION_RECEIPT} resource={'voucher'}>
                        <LoadingButton
                          className='button'
                          variant='contained'
                          disabled={!isAvailable(selectedIds, data, currentAmoutToPaid)}
                          onClick={generatePaymentWithReceipt}
                          loading={loadingGeneratePaymentWithReceipt}
                          style={{ margin: '10px' }}
                        >
                          {translate('ra.action.finish_and_generate_receipt')}
                        </LoadingButton>
                      </HasPermission>
                    )}

                    <LoadingButton
                      className='button'
                      variant='contained'
                      disabled={!isAvailable(selectedIds, data, currentAmoutToPaid)}
                      onClick={generatePayment}
                      loading={loadingGeneratePayment}
                      style={{ margin: '10px' }}
                    >
                      {hasPermissionFunction('voucher', PERMISSION_RECEIPT)
                        ? translate('ra.action.finish_without_generate_receipt')
                        : translate('ra.action.finish')}
                    </LoadingButton>
                  </Box>
                </Box>
              )}
              <ConfirmationDialog
                title={translate('ra.title.credit_in_favor')}
                contentText={translate('ra.message.credit_in_favor_item_message', {
                  total_amount: amountToPay,
                  item_total_amount: amountPending,
                  rest_amount: restAmount,
                  currency: currency?.symbol,
                })}
                buttonConfirmText={translate('ra.action.to_confirm')}
                buttonCancel={translate('ra.action.cancel')}
                setOpen={setOpen}
                setConfirm={setConfirm}
                open={open}
              />
            </>
          )}
        </ListContextProvider>
      </CardCommon>
    </Container>
  )
}
